import { createContext, useContext, useEffect, useRef } from 'react';

/**
 * Lets a screen with unsaved work intercept navigation (sidebar, in-app
 * buttons, browser Back). Value: setGuard(fn | null) from App.
 * The guard receives `proceed` and calls it once leaving is OK.
 */
export const NavigationGuardContext = createContext(() => {});

/**
 * While `active`, route every navigation through `onAttempt(proceed)` and warn
 * on tab close / reload.
 */
export const useNavigationGuard = (active, onAttempt) => {
    const setGuard = useContext(NavigationGuardContext);
    const handler = useRef(onAttempt);
    handler.current = onAttempt;

    useEffect(() => {
        if (!active) return undefined;
        setGuard((proceed) => handler.current(proceed));

        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => {
            setGuard(null);
            window.removeEventListener('beforeunload', onBeforeUnload);
        };
    }, [active, setGuard]);
};
